// ═══════════════════════════════════════════════════════════════
// VACACIONES — Tab Balance
// ═══════════════════════════════════════════════════════════════

import { useState, Fragment } from 'react';
import { SlidersHorizontal, RefreshCw, Save, X, Users, ChevronDown } from 'lucide-react';

const YEAR_OPTIONS = [2023, 2024, 2025, 2026, 2027];

export default function BalanceTab({ balances = [], loading, year, onYearChange, onAllocate, onAdjust, readOnly = false }) {
  const [adjustingId, setAdjustingId]     = useState(null);
  const [adjustForm, setAdjustForm]       = useState({ days: '', notes: '', field: 'allocated' });
  const [adjustSaving, setAdjustSaving]   = useState(false);
  const [adjustError, setAdjustError]     = useState('');
  const openAdjust = (employeeId) => {
    setAdjustingId(employeeId);
    setAdjustForm({ days: '', notes: '', field: 'used' });
    setAdjustError('');
  };

  const handleAdjustSave = async (employeeId) => {
    const days = parseFloat(adjustForm.days);
    if (isNaN(days) || adjustForm.days === '') {
      setAdjustError('Ingresa una cantidad de días válida.');
      return;
    }
    setAdjustSaving(true);
    setAdjustError('');
    try {
      await onAdjust(employeeId, days, adjustForm.notes, adjustForm.field);
      setAdjustingId(null);
    } catch (err) {
      setAdjustError(err?.response?.data?.error || err?.message || 'Error al ajustar.');
    } finally {
      setAdjustSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header con selector de año y botón global */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Año:</label>
          <div className="relative">
            <select
              value={year}
              onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
              className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white appearance-none cursor-pointer"
            >
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        <div />
      </div>

      {/* Contenido */}
      {loading ? (
        <LoadingState />
      ) : balances.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Empleado</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Fecha ingreso</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Asignados</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Usados</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Disponibles</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Pagados</th>
                  {!readOnly && <th className="px-4 py-3 text-right font-semibold text-gray-700">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {balances.map(balance => (
                  <Fragment key={balance.employee_id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{balance.employee_name}</div>
                        {balance.position_name && (
                          <div className="text-xs text-gray-400 mt-0.5">{balance.position_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {balance.hire_date
                          ? new Date(balance.hire_date.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 font-medium">
                        {balance.days_allocated ?? 0}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {balance.days_used ?? 0}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">
                        <span className={(balance.days_available ?? 0) > 0 ? 'text-green-600' : 'text-red-600'}>
                          {balance.days_available ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {balance.days_paid ?? 0}
                      </td>
                      {!readOnly && (
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => adjustingId === balance.employee_id
                                ? setAdjustingId(null)
                                : openAdjust(balance.employee_id)
                              }
                              className={`p-1.5 rounded transition-colors ${
                                adjustingId === balance.employee_id
                                  ? 'text-teal-600 bg-teal-50'
                                  : 'text-gray-400 hover:text-teal-600 hover:bg-teal-50'
                              }`}
                              title="Ajustar balance"
                            >
                              <SlidersHorizontal size={15} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>

                    {/* Fila expandible de ajuste */}
                    {adjustingId === balance.employee_id && (
                      <tr className="bg-teal-50">
                        <td colSpan={7} className="px-4 py-4">
                          <AdjustForm
                            employeeId={balance.employee_id}
                            employeeName={balance.employee_name}
                            form={adjustForm}
                            onChange={(field, value) => setAdjustForm(prev => ({ ...prev, [field]: value }))}
                            onSave={handleAdjustSave}
                            onCancel={() => setAdjustingId(null)}
                            saving={adjustSaving}
                            error={adjustError}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Formulario de ajuste inline ─────────────────────────────────
function AdjustForm({ employeeId, employeeName, form, onChange, onSave, onCancel, saving, error }) {
  return (
    <div className="max-w-2xl">
      <p className="text-xs font-semibold text-teal-700 mb-3 uppercase tracking-wide">
        Ajustar balance — {employeeName}
      </p>
      {error && (
        <p className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Días <span className="text-gray-400">(+ agregar / − restar)</span>
          </label>
          <input
            type="number"
            value={form.days}
            onChange={(e) => onChange('days', e.target.value)}
            placeholder="Ej. 5 o -2"
            step={0.5}
            className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
          <input
            type="text"
            value={form.notes}
            onChange={(e) => onChange('notes', e.target.value)}
            placeholder="Motivo del ajuste…"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onSave(employeeId)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            <Save size={14} />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          <button
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <X size={14} />
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
      <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <Users size={26} className="text-teal-400" />
      </div>
      <p className="text-gray-700 font-medium mb-1">No hay registros para este año</p>
      <p className="text-sm text-gray-500">
        Use "Asignar días a todos" para inicializar los balances de vacaciones.
      </p>
    </div>
  );
}
